/*
 * @Author: cjw 
 * @Date: 2021-03-30 08:35:43 
 * @Last Modified by: cjw
 * @Last Modified time: 2021-09-16 18:08:56
 */

import { ProxyBinding } from "./proxy-binding"

/*
 * 提供数据字典功能
 *
 * 提供以下接口
 * 1. primaryKey     获取主键列数组
 * 2. visibleColumns 获取可视列数组
*/

// 是否对象
function isObject(obj) {
    return typeof obj === 'object' && obj !== null
}

// 是否为数组的下标（索引）
function isArrayIndex(obj) {
    return /^\d+$/.test(obj)
}

// 取出表达式中使用到的字段名称
function buildFieldNames(strExpression, columns) {
    const result = []
    const names = strExpression.match(/[a-zA-Z0-9\u4e00-\u9fa5_]+/g)
    columns.forEach(el => {
        if (names?.indexOf(el.field_name) !== -1) {
            result.push(el.field_name)
        }
    })
    return result
}

// 构造表达式对象
function buildExpression(strExpression, columns) {
    // 生成 exec 函数体里的局部变量（为了方便使用，表达式可以直接使用字段名访问字段值）
    const strValues = buildFieldNames(strExpression, columns)?.map(fieldName => {
        return `const ${fieldName} = this.dataRow["${fieldName}"]`
    }).join('\r\n        ') || ''
    
    // 使用时提供 dataRow 变量，并调用 exec 返回计算结果
    const jsScript = 
        `({\r\n` +
        `    dataRow: null,\r\n` +
        `    exec: function () {\r\n`+
        `        ${strValues}\r\n`+
        `        ${strExpression}\r\n`+
        `    }\r\n` +
        `})`

    return eval(jsScript);
}

function initExpression(target, meta) {
    const calcExpression = {}
    const checkExpression = {}
    target.columns.forEach(el => {

        // 举例：calcExpression 对象格式
        // const calcExpression = {
        //     // 使用字段名作为属性名称，例如此处的 qty，代表 qty 字段涉及到的表达式
        //     qty: [
        //         // field_name 是计算表达式对应的字段，即计算结果将保存到此字段
        //         { field_name: 'money', expression: {} }
        //     ],
        //     price: [
        //         { field_name: 'money', expression: {} }
        //     ]
        // }

        if (el.expression_calc) {
            // 构造计算表达式
            const fieldNames = buildFieldNames(el.expression_calc, target.columns)
            const expression = buildExpression(el.expression_calc, target.columns)
            const fieldExpression = { field_name: el.field_name, expression}
            fieldNames.forEach(fieldName => {
                if (!calcExpression[fieldName]) {
                    calcExpression[fieldName] = []
                }
                calcExpression[fieldName].push(fieldExpression)
            })
        }

        if (el.expression_check) {
            // 构造校验表达式
            const fieldNames = buildFieldNames(el.expression_check, target.columns)
            const expression = buildExpression(el.expression_check, target.columns)
            const fieldExpression = { field_name: el.field_name, expression}
            fieldNames.forEach(fieldName => {
                if (!checkExpression[fieldName]) {
                    checkExpression[fieldName] = []
                }
                checkExpression[fieldName].push(fieldExpression)
            })
        }
    })

    // 计算表达式
    meta.calcExpression = calcExpression,
    // 校验表达式
    meta.checkExpression = checkExpression
}

// 获取校验表达式提示信息（如果表达式不返回错误信息，而是返回布尔值时，将表达式“汉化”并显示）
// 例如：表达式：return qty > 0，则显示为：必须符号以下条件：数量 > 0
function getCheckMessage(strExpression, columns) {
    let result = '';
    if (strExpression) {
        result = strExpression;

        columns.forEach(column => {
            result = result.replace(new RegExp(column.field_name, 'gm'), column.caption)
        });

        result = result
            .replace(/return /g, '必须符合以下条件：')
            .replace(/&&/g, '并且')
            .replace(/\|\|/g, '或者')
            .replace(/.length/g, '的长度')
    }
    return result;
}

function createGetErrors(target, meta) {
    return function getErrors() {
        switch (arguments.length) {
            // 所有错误信息（不传参）
            case 0: {
                const result = []
                target.datas.forEach(data => {
                    const rowError = meta.errors.get(data)
                    if (rowError) {
                        result.push(rowError)
                    }
                })
                return result
            }
            // 数据行的错误信息（传递行对象）
            case 1:
                return meta.errors.get(arguments[0])
            // 字段的错误信息（传递行对象和字段名）
            case 2: {
                const rowError = meta.errors.get(arguments[0]) ?? {}
                return rowError[arguments[1]] ?? ''
            }
            default:
                break;
        }
    }
}

function getProxy(target, meta) {
    // 如果对象已有代理，则返回代理
    let observed = meta.toProxy.get(target)
    if (observed !== void 0) {
        return observed
    }

    if (meta.toRaw.get(target)) {
        return target
    }
}

function createSetColumns(target, meta) {
    return function setColumns(columns) {
        target.columns.length = 0
        target.columns.push(...columns)

        initExpression(target, meta)
    }
}

// 创建响应式对象
function createReactiveDataRow(table, target, meta) {

    // 如果对象已有代理，则返回代理
    const proxyObject = getProxy(target, meta)
    if (proxyObject) {
        return proxyObject
    }

    // 创建 proxy 的 get 处理
    const createGetter = function() {
        return function get(target, property, receiver) {

            // todo: 在这里添加取值时的数据处理

            return Reflect.get(target, property, receiver)
        }
    }

    // 创建 proxy 的 set 处理，在这里添加赋值时的数据处理
    const createSetter = function() {
        return function set(target, property, value, receiver) {

            // 处理小数位（根据格式化字符串“.”后面的位数决定小数位的位数）
            const column = table.columns.find(el => el.field_name === property)
            if (column?.data_type === 'number' && column?.display_format) {
                if (column.display_format.indexOf('.') > 0) {
                    // 获取小数位数
                    const decimalFormat = column.display_format.split('.')[1]
                    const decimalDigits = decimalFormat.length

                    value = parseFloat(parseFloat(value || 0).toFixed(decimalDigits))
                }
            }

            const result = Reflect.set(target, property, value, receiver)

            if (result) {
                if (meta.calcExpression[property]) {
                    // 此字段存在计算表达式
                    meta.calcExpression[property].forEach(el => {
                        el.expression.dataRow = target

                        // 直接赋值不会触发被赋值字段的代理方法，如果出现连动计算表达式时就会出现问题
                        // 例如：金额 = 数量 * 单价、含税金额 = 金额 + 税金，如果输入数量或单价时并不触发金额，则含税金额不会计算
                        // target[el.field_name] = el.expression.exec()
                        // 使用 receiver 对象则可以影响
                        receiver[el.field_name] = el.expression.exec()
                    })
                }

                if (meta.checkExpression[property]) {
                    // 此字段存在校验表达式
                    meta.checkExpression[property].forEach(el => {
                        el.expression.dataRow = target

                        let message = ''
                        const result = el.expression.exec()
                        // 返回 undefined 的话认为是未命中条件，当成是校验通过了
                        if (result !== undefined) {
                            // 返回结果如果是字符串则直接显示，否则对表达式进行“汉化”显示
                            if (Object.prototype.toString.call(result) === '[object String]') {
                                message = result
                            } else {
                                if (!result) {
                                    message = getCheckMessage(column.expression_check, table.columns)
                                    // return this.getCheckMessage();
                                }
                            }

                        }

                        const rowError = meta.errors.get(target) || {}
                        if (message) {
                            rowError[property] = message
                        } else {
                            delete rowError[property]
                        }
                        // 存在错误时更新，否则删除
                        if (Object.keys(rowError).length) {
                            meta.errors.set(receiver, rowError)
                        } else {
                            meta.errors.delete(receiver)
                        }
                    })
                }
            }

            return result
        }
    }

    const handlers = {
        get: createGetter(),
        set: createSetter()
    }

    // 创建 Proxy 代理
    const observed = new Proxy(target, handlers)
    meta.toProxy.set(target, observed)
    meta.toRaw.set(observed, target)
    return observed
}

function createReactiveDataTable(table, target, meta) {

    // 如果对象已有代理，则返回代理
    const proxyObject = getProxy(target, meta)
    if (proxyObject) {
        return proxyObject
    }

    const createGetter = function(meta) {
        return function get(target, property, receiver) {
            const res = Reflect.get(target, property, receiver)
            // 如果是对象，认为是数据行，进行代理
            return isObject(res) ? createReactiveDataRow(table, res, meta) : res
        }
    }

    const createSetter = function(meta) {
        return function set(target, property, value, receiver) {
            if (isArrayIndex(property)) {
                // 由于对记录进行了代理，所以在对原对象进行赋值时，应该取出代理前的值，当新值（尚未代理，则直接使用新值）
                return Reflect.set(target, property, meta.toRaw.get(value) || value, receiver)
            }
    
            // 设置属性值
            return Reflect.set(target, property, value, receiver)
        }
    }

    const handlers = {
        get: createGetter(meta),
        set: createSetter(meta)
    }

    // 创建 Proxy 代理
    const observed = new Proxy(target, handlers)
    meta.toProxy.set(target, observed)
    meta.toRaw.set(observed, target)
    return observed
}

function createReactiveDataSource(target, meta) {
    const createGetter = function(meta) {
        return function get(target, property, receiver) {

            // console.log('获取', target, property);
    
            switch (property) {
                case 'setColumns':
                    return createSetColumns(target, meta)
                case 'primaryKey':
                    // 获取用作数据表主键的列数组
                    return target.columns && target.columns.filter(el => el.primary_key) || []
                case 'visibleColumns':
                    // 获取显示的列（visible 未设值也要显示，只有指定为 False 的才不显示）
                    return target.columns && target.columns.filter(el => el.visible !== false) || []
                // 需要使用别的方法解决，数据将记录再代理一次，会造成编辑数据后删除其它行时数据会变成原值（目前仍不知这是什么原因）
                case 'datas': {
                    const datas = Reflect.get(target, property, receiver)
                    return createReactiveDataTable(target, datas, meta)
                }
                // 拦截加载数据方法，执行计算表达式
                case 'loadDatas': {
                    const loadDatas = Reflect.get(target, property, receiver)
                    return new Proxy(loadDatas, {
                        apply(fun, ctx, args) {
                            // 先执行函数，加载数据后再运行表达式，这样做是为了如果计算表达式运行的结果与加载的数据不一致时会产生脏数据
                            // 否则会出现数据库与程序界面数据不一致的情况，且不编辑相关字段后再保存，数据库永远不会更新，而程序界面又是表达式的结果
                            // 通常这种情况是程序新增了字段又或者改变过计算表达式造成的，出现这种数据时应该更新程序时数据也同步更新
                            let params = []
                            if (args) {
                                params = args
                            }
                            const result = fun(...params)

                            const datas = ctx.datas
                            // 找出所有表达式
                            const calcExpressions = []
                            Object.keys(meta.calcExpression).forEach(key => {
                                meta.calcExpression[key].forEach(el => {
                                    if (calcExpressions.indexOf(el) === -1) {
                                        calcExpressions.push(el)
                                    }
                                })
                            })
                            datas.forEach(data => {
                                calcExpressions.forEach(el => {
                                    el.expression.dataRow = data
                                    data[el.field_name] = el.expression.exec()
                                })
                            })

                            return result                            
                        }
                    })
                }
                case 'getErrors':
                    return createGetErrors(target, meta)
                default:
                    break;
            }
            
            return Reflect.get(target, property, receiver)
        }
    }

    const handlers = {
        get: createGetter(meta)
    }

    return new Proxy(target, handlers)
}

export function ProxyDictionary(target, options) {
    // 为空时初始化默认值
    target = target || {}
    if (!target.columns) {
        target.columns = []
    }

    // 检查参数
    if (!Array.isArray(target.columns)) {
        throw '参数 target 的 columns 属性类型必须是数组'
    }

    const meta = {
        // 选项
        options: options,
        // 已代理的 DataRow 集合（key:原始对象，value:代理对象），用于解决重复代理
        toProxy: new WeakMap(),
        // 已代理的 DataRow 集合（key:代理对象，value:原始对象），与 toProxy 相反
        toRaw: new WeakMap(),
        // 错误信息
        errors: new WeakMap()
    }

    initExpression(target, meta)

    let observed = target
    // 代理数据字典（处理赋值时小数位等）
    observed = createReactiveDataSource(observed, meta)
    // 代理数据绑定
    observed = ProxyBinding(observed, options)

    return observed
}